"use client";
import React from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Link from "next/link";
import PageTransition from "@/components/animations/PageTransition";
import FadeInView from "@/components/animations/FadeInView";
import StaggerContainer from "@/components/animations/StaggerContainer";
import Breadcrumb from "@/components/Breadcrumb";

const challenges = [
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="4" width="20" height="16" rx="2" />
        <path d="M8 20h8" />
        <path d="M12 16v4" />
        <path d="M6 8h.01" />
        <path d="M2 12h20" />
      </svg>
    ),
    title: "POS System Uptime",
    desc: "Every minute of POS downtime costs revenue. We ensure your point-of-sale systems are reliable, monitored, and quickly restored if issues occur.",
  },
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3" />
        <circle cx="4" cy="6" r="2" />
        <circle cx="20" cy="6" r="2" />
        <circle cx="4" cy="18" r="2" />
        <circle cx="20" cy="18" r="2" />
        <path d="M6 7.5L9.5 10.5" />
        <path d="M18 7.5L14.5 10.5" />
        <path d="M6 16.5L9.5 13.5" />
        <path d="M18 16.5L14.5 13.5" />
      </svg>
    ),
    title: "Multi-Site Consistency",
    desc: "Consistent technology standards across all your retail locations — same systems, same security, same performance.",
  },
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="5" width="20" height="14" rx="2" />
        <path d="M2 10h20" />
        <path d="M6 15h4" />
        <path d="M14 15h4" />
      </svg>
    ),
    title: "EFTPOS Connectivity",
    desc: "Reliable network connectivity for EFTPOS terminals — because payment processing downtime is not an option.",
  },
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9h18v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9Z" />
        <path d="m3 9 2.45-4.9A2 2 0 0 1 7.24 3h9.52a2 2 0 0 1 1.8 1.1L21 9" />
        <path d="M12 3v6" />
        <path d="M8 15h.01" />
        <path d="M16 15h.01" />
      </svg>
    ),
    title: "Inventory Systems",
    desc: "Network infrastructure that supports your inventory management and ERP systems across all locations.",
  },
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="3" width="20" height="14" rx="2" />
        <path d="M8 21h8" />
        <path d="M12 17v4" />
      </svg>
    ),
    title: "Staff Device Management",
    desc: "Centrally managed devices for all retail staff — from POS terminals to back-office workstations.",
  },
];

const services = [
  { title: "Infrastructure Deployment", href: "/it-infrastructure", desc: "Complete site setup for new and existing retail locations." },
  { title: "POS & EFTPOS Setup", href: "/it-infrastructure", desc: "Retail technology integration and payment system connectivity." },
  { title: "Network Solutions", href: "/network-solutions", desc: "Reliable network infrastructure across all your retail sites." },
  { title: "Managed IT Services", href: "/managed-it-services", desc: "Proactive IT management and helpdesk support for your retail team." },
  { title: "Cybersecurity", href: "/cyber-security", desc: "Protect customer data and payment systems from cyber threats." },
  { title: "ICT Procurement", href: "/ict-procurement", desc: "Source and deploy the right technology for your retail environment." },
];

export default function RetailPage() {
  return (
    <PageTransition>
      <main style={{ background: "#070B14", overflowX: "hidden" }}>
        <Header />
        <Breadcrumb
          items={[
            { label: "Home", href: "/" },
            { label: "Industries", href: "/industries" },
            { label: "Retail" },
          ]}
        />
        {/* Hero */}
        <section className="relative min-h-[320px] md:min-h-[480px] flex items-center justify-center py-32 overflow-hidden">
          <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, #070B14 0%, #0B1220 40%, #0f1f3d 70%, #070B14 100%)" }} />
          <div className="absolute top-1/3 right-1/4 w-96 h-96 rounded-full blob-1" style={{ background: "radial-gradient(circle, rgba(37,99,235,0.15) 0%, transparent 70%)" }} />
          <div className="relative max-w-container mx-auto px-4 sm:px-6 text-center">
            <FadeInView delay={0}>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-pill mb-8" style={{ background: "rgba(37,99,235,0.12)", border: "1px solid rgba(59,130,246,0.25)" }}>
                <span className="mr-1" style={{ color: "#60A5FA" }}><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9V21h18V9" /><path d="M9 21V12h6v9" /><path d="M3 9l4-6h10l4 6" /><path d="M7 3v6" /><path d="M17 3v6" /><path d="M12 3v6" /></svg></span>
                <span className="text-[11px] font-body font-600 uppercase tracking-widest gradient-text">Retail Industry</span>
              </div>
            </FadeInView>
            <FadeInView delay={0.15}>
              <h1 className="font-heading font-800 text-display-md md:text-display mb-6" style={{ color: "#F8FAFC", letterSpacing: "-0.03em", lineHeight: "1.05" }}>
                Reliable IT for Retail Businesses<br />
                <span className="gradient-text">Across New Zealand</span>
              </h1>
            </FadeInView>
            <FadeInView delay={0.3}>
              <p className="text-body-lg leading-[1.75] max-w-3xl mx-auto mb-10" style={{ color: "#94A3B8" }}>
                Pro IT Solutions understands the technology demands of retail — from POS uptime and EFTPOS connectivity to multi-site network management and staff device support.
              </p>
            </FadeInView>
            <FadeInView delay={0.45}>
              <Link href="/book" className="px-6 sm:px-8 py-3.5 sm:py-4 text-[15px] font-heading font-600 rounded-pill btn-primary">Book a Retail IT Assessment</Link>
            </FadeInView>
          </div>
        </section>

        {/* Challenges */}
        <section className="py-20 lg:py-28" style={{ background: "#0B1220" }}>
          <div className="max-w-container mx-auto px-4 sm:px-6">
            <FadeInView className="text-center mb-16">
              <h2 className="font-heading font-700 text-[24px] sm:text-[36px] leading-[1.05] mb-4" style={{ color: "#F8FAFC" }}>Retail IT Challenges We Solve</h2>
            </FadeInView>
            {/* Desktop grid */}
            <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch" staggerDelay={0.08}>
              {challenges?.map((c) => (
                <div key={c?.title} className="glass-card rounded-card p-6 h-full flex flex-col">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4" style={{ background: "rgba(59,130,246,0.12)", color: "#60A5FA" }}>{c?.icon}</div>
                  <h3 className="font-heading font-700 text-[24px] mb-3" style={{ color: "#F8FAFC" }}>{c?.title}</h3>
                  <p className="text-body-sm leading-[1.75]" style={{ color: "#94A3B8" }}>{c?.desc}</p>
                </div>
              ))}
            </StaggerContainer>
          </div>
        </section>

        {/* Services */}
        <section className="py-20">
          <div className="max-w-container mx-auto px-4 sm:px-6">
            <FadeInView className="text-center mb-16">
              <h2 className="font-heading font-700 text-[24px] sm:text-[36px] leading-[1.05] mb-4" style={{ color: "#F8FAFC" }}>Services for Retail Businesses</h2>
            </FadeInView>
            {/* Desktop grid */}
            <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch" staggerDelay={0.08}>
              {services?.map((svc) => (
                <Link key={svc?.title} href={svc?.href} className="group rounded-card-lg p-6 transition-all duration-300 hover:scale-[1.02] h-full flex flex-col" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}>
                  <h3 className="font-heading font-700 text-[24px] mb-2" style={{ color: "#F8FAFC" }}>{svc?.title}</h3>
                  <p className="text-[14px] font-body mb-4 leading-[1.75]" style={{ color: "#94A3B8" }}>{svc?.desc}</p>
                  <span className="text-[14px] font-body font-medium mt-auto" style={{ color: "#60A5FA" }}>Learn more →</span>
                </Link>
              ))}
            </StaggerContainer>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20">
          <div className="max-w-container mx-auto px-4 sm:px-6">
            <FadeInView scale={0.95}>
              <div className="rounded-card-lg p-6 sm:p-10 lg:p-16 text-center" style={{ background: "rgba(37,99,235,0.08)", border: "1px solid rgba(59,130,246,0.2)" }}>
                <h2 className="font-heading font-700 text-[24px] sm:text-[36px] leading-[1.05] mb-4" style={{ color: "#F8FAFC" }}>Ready to improve your retail IT?</h2>
                <p className="text-body-lg mb-8 leading-[1.75]" style={{ color: "#94A3B8" }}>Let&apos;s discuss your retail technology requirements and build a solution that keeps your business running.</p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link href="/book" className="px-6 sm:px-8 py-3.5 sm:py-4 rounded-pill font-heading font-600 btn-primary">Book a Free Assessment</Link>
                  <Link href="/services" className="px-6 sm:px-8 py-3.5 sm:py-4 rounded-pill font-heading font-500 btn-secondary">View All Services</Link>
                </div>
              </div>
            </FadeInView>
          </div>
        </section>

        <Footer />
      </main>
    </PageTransition>
  );
}
